import { useEffect, useMemo, useRef, useState } from 'react';
import { TwelveDataProxySocket } from '../utils/TwelveDataProxySocket';

/**
 * useTwelveDataPairsTicker
 * ------------------------
 * Mirrors the shape returned by useBinancePairsTicker but sources prices from
 * the TwelveDataProxySocket (backend /prices WS gateway → Twelve Data).
 *
 * @param {Array}  pairs    - Array of pair objects: { symbol, label, icon?, fallbackPrice? }
 * @returns {Array}         - Same enriched pair-stats array as useBinancePairsTicker
 */
export function useTwelveDataPairsTicker(pairs) {
    // 🔧 FIX: Stabilize safePairs with useMemo so its reference only changes
    //         when the actual pair symbols change — not on every render.
    //         Previously `safePairs = Array.isArray(pairs) ? pairs : []` was
    //         inlined, producing a new array identity every render, causing
    //         cascading re-subscriptions and infinite useMemo recomputations.
    const safePairs = useMemo(
        () => (Array.isArray(pairs) ? pairs : []),
        // Intentionally keyed on the serialized symbol list so the reference
        // stays stable as long as the set of symbols doesn't change.
        [pairs?.map((p) => p.symbol).join(',')]
    );

    const [statsBySymbol, setStatsBySymbol] = useState(() => buildInitialStatsMap(safePairs));

    // 🔧 FIX: Derive a stable string key to use as the single dependency for
    //         effects that should react to the pair list changing.
    //         This avoids depending on the `safePairs` array reference directly,
    //         which would re-fire effects on every render.
    const symbolKey = safePairs.map((p) => p.symbol).join(',');

    // Keep stats in sync when the pairs list itself changes
    useEffect(() => {
        setStatsBySymbol((prev) => reconcileStatsMap(prev, safePairs));
    }, [symbolKey]); // 🔧 FIX: depend on the stable key, not on safePairs reference

    // 🔧 FIX: Persist the onUpdate handler in a ref so the socket subscription
    //         effect never needs to re-run when the handler identity changes
    //         (e.g. after a state update causes a re-render and closure refresh).
    const onUpdateRef = useRef(null);

    // Keep the handler ref current without triggering re-subscription
    useEffect(() => {
        onUpdateRef.current = (symbol, tick) => {
            setStatsBySymbol((prev) => {
                const existing = prev[symbol];

                const price = firstFinite(tick?.price, existing?.price, existing?.fallbackPrice);
                const openPrice = firstFinite(tick?.openPrice, existing?.openPrice, price);
                const highPrice = firstFinite(tick?.highPrice, existing?.highPrice, price);
                const lowPrice = firstFinite(tick?.lowPrice, existing?.lowPrice, price);
                const changePercent = Number.isFinite(tick?.changePercent)
                    ? tick.changePercent
                    : deriveChangePercent(price, openPrice, existing?.changePercent);

                // 🔧 FIX: Bail out early if no values actually changed to avoid
                //         triggering a re-render for an identical state object.
                if (
                    existing &&
                    existing.price === price &&
                    existing.openPrice === openPrice &&
                    existing.highPrice === highPrice &&
                    existing.lowPrice === lowPrice &&
                    existing.changePercent === (Number.isFinite(changePercent) ? changePercent : existing.changePercent)
                ) {
                    return prev; // 🔧 FIX: return the same reference → React skips re-render
                }

                return {
                    ...prev,
                    [symbol]: {
                        ...existing,
                        price,
                        openPrice,
                        highPrice,
                        lowPrice,
                        spread: calcSpread(highPrice, lowPrice),
                        spreadPercent: calcSpreadPercent(highPrice, lowPrice),
                        changePercent: Number.isFinite(changePercent) ? changePercent : existing?.changePercent,
                        direction: resolveDirection(price, openPrice, changePercent),
                        isLive: true,
                    },
                };
            });
        };
    }); // no deps — runs after every render, but just updates a ref (no state change)

    // 🔧 FIX: Socket subscription effect depends only on the stable symbolKey.
    //         The actual onUpdate logic is read from onUpdateRef.current so the
    //         socket is subscribed ONCE per symbol-set change and never re-fires
    //         because of the handler closure being recreated.
    useEffect(() => {
        if (safePairs.length === 0 || safePairs[0].price_source === 'binance') return;

        // Stable wrapper that always delegates to the latest handler via ref
        const stableOnUpdate = (symbol, tick) => {
            onUpdateRef.current?.(symbol, tick);
        };

        const socket = new TwelveDataProxySocket({ onUpdate: stableOnUpdate });
        socket.connect();
        socket.subscribe(safePairs.map((p) => p.symbol));

        // 🔧 FIX: Always clean up the previous socket before creating a new one,
        //         preventing duplicate subscriptions / ghost listeners.
        return () => {
            socket.disconnect();
        };
    }, [symbolKey]); // 🔧 FIX: depend on stable key — not on safePairs or the handler

    // 🔧 FIX: useMemo now depends on the stable `safePairs` (stabilized by the
    //         outer useMemo) and `statsBySymbol`, preventing recomputation on
    //         every render that doesn't change meaningful data.
    return useMemo(
        () => {
            console.log("🚀 ~ useTwelveDataPairsTicker ~ safePairs:", safePairs?.find(p => p.symbol === 'EURUSD'), statsBySymbol['EUR/USD'])
            return safePairs.map((pair) => ({ ...statsBySymbol[pair.symbol], ...pair }));
        },
        [safePairs, statsBySymbol],
    );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildInitialStatsMap(pairs) {
    const map = {};
    for (const pair of pairs) {
        map[pair.symbol] = createPairStats(pair);
    }
    return map;
}

function reconcileStatsMap(prev, pairs) {
    // 🔧 FIX: Return the previous reference unchanged when the pairs list is
    //         empty, instead of always returning a new `{}` object which would
    //         trigger a downstream re-render even when nothing changed.
    if (!Array.isArray(pairs) || pairs.length === 0) return prev;

    const map = {};
    for (const pair of pairs) {
        if (pair.price_source === 'binance') continue;
        map[pair.symbol] = createPairStats(pair, prev[pair.symbol]);
    }
    return map;
}

function createPairStats(pair, existing = null) {
    const price = firstFinite(existing?.price, pair.fallbackPrice);
    const openPrice = firstFinite(existing?.openPrice, pair.fallbackPrice, price);
    const highPrice = firstFinite(existing?.highPrice, pair.fallbackPrice, price);
    const lowPrice = firstFinite(existing?.lowPrice, pair.fallbackPrice, price);
    const changePercent = Number.isFinite(existing?.changePercent)
        ? existing.changePercent
        : deriveChangePercent(price, openPrice, 0);

    return {
        symbol: pair.symbol,
        label: pair.label,
        icon: pair.icon || existing?.icon || '',
        fallbackPrice: pair.fallbackPrice,
        price,
        openPrice,
        highPrice,
        lowPrice,
        spread: calcSpread(highPrice, lowPrice),
        spreadPercent: calcSpreadPercent(highPrice, lowPrice),
        changePercent: Number.isFinite(changePercent) ? changePercent : 0,
        direction: resolveDirection(price, openPrice, changePercent),
        isLive: Boolean(existing?.isLive),
    };
}

function deriveChangePercent(price, openPrice, fallback = 0) {
    if (Number.isFinite(price) && Number.isFinite(openPrice) && openPrice !== 0) {
        return ((price - openPrice) / openPrice) * 100;
    }
    return Number.isFinite(fallback) ? fallback : 0;
}

function calcSpread(high, low) {
    return Number.isFinite(high) && Number.isFinite(low) ? Math.max(0, high - low) : 0;
}

function calcSpreadPercent(high, low) {
    if (!Number.isFinite(low) || low <= 0) return 0;
    return (calcSpread(high, low) / low) * 100;
}

function resolveDirection(price, openPrice, changePercent) {
    if (Number.isFinite(price) && Number.isFinite(openPrice) && price !== openPrice) {
        return price > openPrice ? 'up' : 'down';
    }
    if (Number.isFinite(changePercent) && changePercent !== 0) {
        return changePercent > 0 ? 'up' : 'down';
    }
    return 'flat';
}

function firstFinite(...values) {
    for (const v of values) {
        if (Number.isFinite(v)) return v;
    }
    return NaN;
}
